import type { EventContextField } from './eventContextSchemas'
import {
  CrmOperationSchema,
  type CrmOperation,
  type TriggerMatchConfig,
} from './resources/types'

export type ParsedAppEventInputType = {
  /** App handle from @app/{handle}/... */
  appHandle: string
  /** Event name with dots, e.g. member.updated or member.* */
  eventName: string
  /** Canonical subscription event type, e.g. app.bft.member.updated */
  eventType: string
  /** True when the last segment is * (wildcard) */
  isWildcard: boolean
  /** Original @app/... type string */
  inputType: string
}

export type AppEventInputTypeMetadata = {
  inputType: string
  appHandle: string
  eventName: string
  examplePayload?: Record<string, unknown>
  /** Liquid paths scoped for workflow steps: inputs.data.phone */
  workflowInputFields: EventContextField[]
}

const APP_EVENT_INPUT_PREFIX = '@app/'

/**
 * Resource picker (From Phone), not an @app/{handle}/{event} payload type.
 * `@app/communication-channel/sms` would otherwise parse as
 * `app.communication-channel.sms` and fail CRM `instance.updated` trigger save.
 */
export function isCommunicationChannelWorkflowInput(
  type: string | undefined,
  inputName?: string,
): boolean {
  if (inputName === 'communication-channel-id') {
    return true
  }
  if (!type) {
    return false
  }
  return (
    type === '@app/communication-channel' ||
    type.startsWith('@app/communication-channel/')
  )
}

/**
 * Parse workflow input types like @app/bft/member/updated → app.bft.member.updated
 */
export function parseAppEventInputType(
  type: string,
): ParsedAppEventInputType | null {
  if (!type.startsWith(APP_EVENT_INPUT_PREFIX)) {
    return null
  }

  if (isCommunicationChannelWorkflowInput(type)) {
    return null
  }

  const rest = type.slice(APP_EVENT_INPUT_PREFIX.length)
  const segments = rest.split('/').filter(Boolean)
  if (segments.length < 2) {
    return null
  }

  const appHandle = segments[0]!
  const eventSegments = segments.slice(1)
  const isWildcard = eventSegments.at(-1) === '*'
  const eventName = eventSegments.join('.').replace(/\.\*$/, '.*')
  const eventTypeSuffix = isWildcard
    ? eventSegments.slice(0, -1).join('.')
    : eventSegments.join('.')

  return {
    appHandle,
    eventName,
    eventType: `app.${appHandle}.${eventTypeSuffix}${isWildcard ? '' : ''}`,
    isWildcard,
    inputType: type,
  }
}

/**
 * Whether a workflow @app/... input type matches a subscription event type.
 * Supports wildcards: @app/bft/member/* ↔ app.bft.member.updated
 */
export function appEventInputTypeMatchesEventType(
  inputType: string,
  subscriptionEventType: string,
): boolean {
  const parsedInput = parseAppEventInputType(inputType)
  if (!parsedInput || !subscriptionEventType.startsWith('app.')) {
    return false
  }

  const subParts = subscriptionEventType.split('.')
  if (subParts.length < 3 || subParts[1] !== parsedInput.appHandle) {
    return false
  }

  const subEventName = subParts.slice(2).join('.')

  if (parsedInput.isWildcard) {
    // Require an entity boundary so `member.*` does not match `membership.*`.
    const entityPrefix = parsedInput.eventName.replace(/\.\*$/, '')
    return (
      subEventName === entityPrefix ||
      subEventName.startsWith(`${entityPrefix}.`)
    )
  }

  return subEventName === parsedInput.eventName
}

/**
 * Derive @app/{handle}/{event/path} from catalog event name (member.updated).
 */
export function deriveWorkflowInputType(
  appHandle: string,
  eventName: string,
): string {
  return `${APP_EVENT_INPUT_PREFIX}${appHandle}/${eventName.replace(/\./g, '/')}`
}

function rewriteFieldPathForWorkflowInput(
  path: string,
  inputName: string,
): string {
  if (path === 'data') {
    return `inputs.${inputName}`
  }
  if (path.startsWith('data.')) {
    return `inputs.${inputName}.${path.slice('data.'.length)}`
  }
  return `inputs.${inputName}.${path}`
}

function rewriteContextFieldsForWorkflowInput(
  fields: EventContextField[],
  inputName: string,
): EventContextField[] {
  return fields.map((field) => ({
    ...field,
    path: rewriteFieldPathForWorkflowInput(field.path, inputName),
    children: field.children
      ? rewriteContextFieldsForWorkflowInput(field.children, inputName)
      : undefined,
  }))
}

/**
 * Build workflow-step liquid field tree under inputs.{inputName}.* from catalog context fields.
 */
export function buildWorkflowInputFieldsFromCatalog(
  catalogContextFields: EventContextField[] | undefined,
  inputName: string,
  examplePayload?: Record<string, unknown>,
): EventContextField[] {
  if (catalogContextFields?.length) {
    return [
      {
        path: `inputs.${inputName}`,
        label: 'Event data',
        type: 'object',
        children: rewriteContextFieldsForWorkflowInput(
          catalogContextFields,
          inputName,
        ),
      },
    ]
  }

  if (examplePayload && typeof examplePayload === 'object') {
    const childFields = Object.keys(examplePayload).map((key) => ({
      path: `inputs.${inputName}.${key}`,
      label: key.replace(/_/g, ' '),
      type:
        examplePayload[key] != null && typeof examplePayload[key] === 'object'
          ? 'object'
          : typeof examplePayload[key],
    }))

    return [
      {
        path: `inputs.${inputName}`,
        label: 'Event data',
        type: 'object',
        children: childFields,
      },
    ]
  }

  return [
    {
      path: `inputs.${inputName}`,
      label: 'Event data',
      type: 'object',
    },
  ]
}

export type AppEventCatalogEntry = {
  name: string
  label?: string
  workflowInputType?: string
  examplePayload?: Record<string, unknown>
  contextFields?: EventContextField[]
}

/**
 * Resolve catalog metadata for a workflow @app/... input type.
 */
export function resolveAppEventInputTypeMetadata(
  inputType: string,
  catalogEvents: AppEventCatalogEntry[],
  inputName = 'data',
): AppEventInputTypeMetadata | null {
  const parsed = parseAppEventInputType(inputType)
  if (!parsed) {
    return null
  }

  const catalogEntry = catalogEvents.find((event) => {
    if (event.workflowInputType === inputType) {
      return true
    }
    const derived = deriveWorkflowInputType(parsed.appHandle, event.name)
    if (parsed.isWildcard) {
      const prefix = parsed.eventName.replace(/\.\*$/, '.')
      return event.name.startsWith(prefix)
    }
    return derived === inputType || event.name === parsed.eventName
  })

  return {
    inputType,
    appHandle: parsed.appHandle,
    eventName: parsed.eventName,
    examplePayload: catalogEntry?.examplePayload,
    workflowInputFields: buildWorkflowInputFieldsFromCatalog(
      catalogEntry?.contextFields,
      inputName,
      catalogEntry?.examplePayload,
    ),
  }
}

/**
 * Find workflow inputs that declare @app/... event payload types.
 */
export function findAppEventWorkflowInputs(
  inputs: Record<string, { type?: string }>,
): Array<{ name: string; type: string; parsed: ParsedAppEventInputType }> {
  const result: Array<{
    name: string
    type: string
    parsed: ParsedAppEventInputType
  }> = []

  for (const [name, schema] of Object.entries(inputs)) {
    const type = schema.type
    if (!type) continue
    if (isCommunicationChannelWorkflowInput(type, name)) continue
    const parsed = parseAppEventInputType(type)
    if (parsed) {
      result.push({ name, type, parsed })
    }
  }

  return result
}

export const DEFAULT_APP_EVENT_INPUT_MAPPING = '{{ data }}'

function titleCaseEntity(entity: string): string {
  if (!entity) return entity
  return entity.charAt(0).toUpperCase() + entity.slice(1).replace(/_/g, ' ')
}

/**
 * Build a TriggerMatchConfig seed from a bus event type like
 * `app.bft.membership.created` so install/setup subscriptions render as
 * sentence-style triggers in the workflow sidebar.
 */
export function buildAppMatchConfigFromEventType(params: {
  eventType: string
  label?: string
  conditions?: TriggerMatchConfig['conditions']
}): TriggerMatchConfig | null {
  const parts = params.eventType.split('.').filter(Boolean)
  if (parts.length < 4 || parts[0] !== 'app') {
    return null
  }

  const appHandle = parts[1]!
  const eventSegments = parts.slice(2)
  const operationRaw = eventSegments[eventSegments.length - 1]!
  const parsedOp = CrmOperationSchema.safeParse(operationRaw)
  if (!parsedOp.success) {
    return null
  }
  const operation: CrmOperation = parsedOp.data

  const entity = eventSegments[0]!
  const appEventName = eventSegments.join('.')
  const label = params.label?.trim() || titleCaseEntity(entity)

  return {
    version: 1,
    source: 'app',
    eventType: params.eventType,
    label,
    appHandle,
    appEventName,
    operation,
    conditions: params.conditions ?? [],
  }
}
